import {useEffect, useState} from 'react';
import {useParams, Link, useHistory, useLocation} from 'react-router-dom';
import {Button, Card, Col, Empty, Row, Spin, Tag, Typography} from 'antd';
import {
  PlusOutlined,
  CalendarOutlined,
  DollarOutlined,
} from '@ant-design/icons';
import {StudentsService} from './StudentsService';
import {EnrollmentsService} from '../Enrollments/EnrollmentsService';
import type {NewEnrollment} from '@PikaDoApp/npm-pikado-logic/dist/modules/enrollment/graphql/enrollment.interface';

const studentsService = new StudentsService();
const enrollmentsService = new EnrollmentsService();

const enrollmentStatusColor: Record<string, string> = {
  ACTIVE: 'green',
  EXPIRED: 'red',
};

const placementStatusColor: Record<string, string> = {
  TODO: 'default',
  DOING: 'processing',
  DONE: 'success',
};

export const StudentDetails = () => {
  const [student, setStudent] = useState<any>(undefined);
  const [enrollments, setEnrollments] = useState<NewEnrollment[]>([]);
  const [isEnrollmentsLoading, setIsEnrollmentsLoading] =
    useState<boolean>(true);
  const [isError, setIsError] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const params: {studentId: string} = useParams();
  const history = useHistory();
  const {Title, Text} = Typography;
  const location = useLocation();

  useEffect(() => {
    studentsService
      .getStudentById(params.studentId)
      .then(resp => {
        setStudent(resp);
        setIsLoading(false);
      })
      .catch(() => {
        setIsLoading(false);
        setIsError(true);
      });

    setIsEnrollmentsLoading(true);
    enrollmentsService
      .getEnrollmentsByStudentId(params.studentId)
      .then(resp => {
        setEnrollments(resp);
        setIsEnrollmentsLoading(false);
      })
      .catch(() => {
        setIsEnrollmentsLoading(false);
      });
  }, [location.pathname]);

  const formatDate = (dateStr?: string | null) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return dateStr;
    }
  };

  const Field = ({label, value}: {label: string; value?: any}) => (
    <div style={{margin: 20}}>
      <Text style={{display: 'block'}} strong={true}>
        {label}
      </Text>
      <Text>{value ?? '-'}</Text>
    </div>
  );

  return (
    <div style={{padding: 10}}>
      <Title style={{textAlign: 'center'}}>Student</Title>
      <Button size={'large'} style={{float: 'right'}}>
        <Link
          to={{
            pathname: '/students/' + student?.id + '/edit',
            state: {student},
          }}>
          Edit
        </Link>
      </Button>

      {!isError && !isLoading && (
        <>
          <Row>
            <Col span={12}>
              <Field label="Student Name" value={student?.name} />
            </Col>
            <Col span={12}>
              <Field label="Student ID" value={student?.id} />
            </Col>
          </Row>

          <Row>
            <Col span={12}>
              <Field label="Gender" value={student?.gender} />
            </Col>
            <Col span={12}>
              <Field label="Age" value={student?.age} />
            </Col>
          </Row>

          <Row>
            <Col span={12}>
              <Field
                label="Birth Date"
                value={formatDate(student?.birthDate)}
              />
            </Col>
            <Col span={12}>
              <Field label="Joined At" value={formatDate(student?.createdAt)} />
            </Col>
          </Row>

          <Row>
            <Col span={12}>
              <Field
                label="School ID"
                value={student?.schoolId ?? student?.school?.id}
              />
            </Col>
            <Col span={12}>
              <Field
                label="Class ID"
                value={
                  student?.classSchoolId ??
                  student?.classschoolId ??
                  student?.classschool?.id
                }
              />
            </Col>
          </Row>

          <Row>
            <Col span={12}>
              <Field
                label="Level ID"
                value={student?.levelId ?? student?.level?.id}
              />
            </Col>
            <Col span={12}>
              <Field
                label="Grade ID"
                value={student?.gradeId ?? student?.grade?.id}
              />
            </Col>
          </Row>

          <Row>
            <Col span={12}>
              <Field
                label="User ID"
                value={student?.userId ?? student?.user?.id}
              />
            </Col>
            <Col span={12}>
              <Field label="User Email" value={student?.user?.email} />
            </Col>
          </Row>

          <Row>
            <Col span={12}>
              <Field
                label="Whatsapp Number"
                value={student?.user?.whatsappNumber}
              />
            </Col>
            <Col span={12}>
              <Field label="Country" value={student?.user?.country} />
            </Col>
          </Row>

          <Row>
            <Col span={24}>
              <div style={{margin: 20}}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 12,
                  }}>
                  <Text strong={true} style={{fontSize: 16}}>
                    Student Enrollments ({enrollments.length})
                  </Text>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() =>
                      history.push({
                        pathname: '/enrollments/create',
                        state: {
                          studentId: params.studentId,
                          studentName: student?.name,
                        },
                      })
                    }>
                    Create New Enrollment
                  </Button>
                </div>
                {isEnrollmentsLoading ? (
                  <Spin
                    size="small"
                    style={{display: 'block', textAlign: 'center', padding: 20}}
                  />
                ) : enrollments.length === 0 ? (
                  <Empty
                    description="No enrollments found"
                    style={{padding: 20}}
                  />
                ) : (
                  enrollments.map(enrollment => (
                    <Card
                      key={enrollment._id}
                      size="small"
                      style={{marginBottom: 8}}
                      hoverable
                      onClick={() =>
                        history.push('/enrollments/' + enrollment._id)
                      }>
                      <Row gutter={16} align="middle">
                        <Col flex="auto">
                          <Link
                            to={'/enrollments/' + enrollment._id}
                            style={{fontWeight: 600, fontSize: 14}}
                            onClick={e => e.stopPropagation()}>
                            {enrollment._id}
                          </Link>
                        </Col>
                        <Col>
                          <Tag
                            color={
                              enrollmentStatusColor[
                                enrollment.enrollmentStatus
                              ] || 'default'
                            }>
                            {enrollment.enrollmentStatus}
                          </Tag>
                        </Col>
                        <Col>
                          <Tag
                            color={
                              placementStatusColor[
                                enrollment.placementStatus
                              ] || 'default'
                            }>
                            Placement: {enrollment.placementStatus}
                          </Tag>
                        </Col>
                      </Row>
                      <Row gutter={16} style={{marginTop: 8}}>
                        <Col span={6}>
                          <Text type="secondary" style={{fontSize: 12}}>
                            <CalendarOutlined /> Start
                          </Text>
                          <br />
                          <Text style={{fontSize: 13}}>
                            {formatDate(enrollment.startDate)}
                          </Text>
                        </Col>
                        <Col span={6}>
                          <Text type="secondary" style={{fontSize: 12}}>
                            <CalendarOutlined /> End
                          </Text>
                          <br />
                          <Text style={{fontSize: 13}}>
                            {formatDate(enrollment.endDate)}
                          </Text>
                        </Col>
                        <Col span={6}>
                          <Text type="secondary" style={{fontSize: 12}}>
                            <DollarOutlined /> Price
                          </Text>
                          <br />
                          <Text style={{fontSize: 13}}>
                            {enrollment.finalPrice
                              ? `${enrollment.finalPrice.price ?? '-'} ${enrollment.finalPrice.currency}`
                              : '-'}
                          </Text>
                        </Col>
                        <Col span={6}>
                          <Text type="secondary" style={{fontSize: 12}}>
                            Payment
                          </Text>
                          <br />
                          <Text style={{fontSize: 13}}>
                            {enrollment.paymentMethod ?? '-'}
                            {enrollment.isFree && (
                              <Tag color="blue" style={{marginLeft: 4}}>
                                Free
                              </Tag>
                            )}
                          </Text>
                        </Col>
                      </Row>
                    </Card>
                  ))
                )}
              </div>
            </Col>
          </Row>
        </>
      )}
      {isLoading && (
        <Spin size="large" style={{margin: 'auto', width: '100%'}} />
      )}
      {isError && <Title>Student fetching error</Title>}
    </div>
  );
};
